import React, { useState, useEffect } from 'react';
import api from '../services/api';
import './TimeOff.css';
import HolidayCustomization from './HolidayCustomization';

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const DAYS_IN_MONTH = [31, 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31];
const WEEKDAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

function TimeOff() {
  const currentYear = new Date().getFullYear();
  const [calendarYear, setCalendarYear] = useState(currentYear); // Selectable year for calendar
  const [balanceYear, setBalanceYear] = useState(currentYear); // Selectable year for balance card
  const [balanceLoading, setBalanceLoading] = useState(false); // Loading state for balance year change
  const [calendarData, setCalendarData] = useState(null);
  const [balance, setBalance] = useState(null);
  const [leaveTypes, setLeaveTypes] = useState([]);
  const [countries, setCountries] = useState([]);
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('calendar');
  const [showAddModal, setShowAddModal] = useState(false);
  const [selectedDate, setSelectedDate] = useState(null);
  const [upcomingEntries, setUpcomingEntries] = useState([]);
  const [allEntries, setAllEntries] = useState([]);
  const [upcomingHolidays, setUpcomingHolidays] = useState([]);
  const [editingEntry, setEditingEntry] = useState(null);
  const [currentJob, setCurrentJob] = useState(null); // Active employment for year period
  const [showEmploymentChangeModal, setShowEmploymentChangeModal] = useState(false);

  // Check if year is leap year
  const isLeapYear = (y) => (y % 4 === 0 && y % 100 !== 0) || (y % 400 === 0);
  
  const getDaysInMonth = (monthIndex, y) => {
    if (monthIndex === 1 && isLeapYear(y)) return 29;
    return DAYS_IN_MONTH[monthIndex];
  };

  useEffect(() => {
    fetchData();
  }, [calendarYear]);

  // Re-fetch balance when balance year changes (separate from calendar year)
  useEffect(() => {
    if (!loading) { // Don't double-fetch on initial load
      fetchBalanceForYear(balanceYear);
    }
  }, [balanceYear]);

  const fetchBalanceForYear = async (year) => {
    setBalanceLoading(true);
    try {
      const [balanceRes, leaveBalanceRes] = await Promise.all([
        api.get(`/timeoff/balance?year=${year}`),
        api.get('/employment/leave-balance').catch(() => ({ data: null }))
      ]);
      const detailedBalance = leaveBalanceRes.data;
      const bData = balanceRes.data;
      setBalance({
        configured: true,
        total_days: bData.total_days ?? 0,
        used_days: bData.used_days ?? 0,
        planned_days: bData.planned_days ?? 0,
        available_days: bData.available_days ?? 0,
        prorata: detailedBalance?.prorata,
        entitlement: detailedBalance?.entitlement
      });
    } catch (err) {
      console.error('Error fetching balance for year:', year, err);
      // Show zero balance but keep configured state
      setBalance(prev => ({
        configured: prev?.configured ?? false,
        total_days: 0,
        used_days: 0,
        planned_days: 0,
        available_days: 0
      }));
    } finally {
      setBalanceLoading(false);
    }
  };

  const fetchData = async () => {
    setLoading(true);
    setError(null);
    try {
      // Fetch current year, previous year, next year entries AND employment data
      const [calendarRes, currentYearCalendarRes, prevYearRes, nextYearRes, balanceRes, leaveBalanceRes, typesRes, countriesRes, settingsRes, jobsRes] = await Promise.all([
        api.get(`/timeoff/calendar/${calendarYear}`),
        api.get(`/timeoff/calendar/${currentYear}`),
        api.get(`/timeoff/calendar/${currentYear - 1}`).catch(() => ({ data: { entries: [] } })),
        api.get(`/timeoff/calendar/${currentYear + 1}`).catch(() => ({ data: { entries: [] } })),
        api.get(`/timeoff/balance?year=${balanceYear}`),
        api.get('/employment/leave-balance').catch(() => ({ data: null })),  // Get detailed leave balance from employment
        api.get('/timeoff/types'),
        api.get('/timeoff/countries'),
        api.get(`/timeoff/settings?year=${currentYear}`).catch(() => ({ data: null })),
        api.get('/employment/jobs').catch(() => ({ data: [] }))
      ]);
      
      // Store detailed leave balance for pro-rata display
      const detailedBalance = leaveBalanceRes.data;
      
      // Find the current/active job for year period calculation
      const jobs = jobsRes.data || [];
      const activeJob = jobs.find(j => j.is_current) || jobs[0] || null;
      setCurrentJob(activeJob);
      
      setCalendarData(calendarRes.data);
      setUpcomingEntries(currentYearCalendarRes.data?.entries || []);
      setUpcomingHolidays(currentYearCalendarRes.data?.holidays || []);
      
      // Combine all entries from multiple years
      const combined = [
        ...(prevYearRes.data?.entries || []),
        ...(currentYearCalendarRes.data?.entries || []),
        ...(nextYearRes.data?.entries || [])
      ];
      // Remove duplicates by id
      const uniqueEntries = combined.filter((entry, index, self) => 
        index === self.findIndex(e => e.id === entry.id)
      );
      setAllEntries(uniqueEntries);
      
      // Merge simple balance with detailed balance for complete info
      setBalance({
        ...balanceRes.data,
        prorata: detailedBalance?.prorata,
        entitlement: detailedBalance?.entitlement
      });
      setLeaveTypes(typesRes.data);
      setCountries(countriesRes.data);
      setSettings(settingsRes.data);
    } catch (err) {
      console.error('Error fetching time off data:', err);
      if (err.response?.status === 404) {
        setCalendarData({ entries: [], holidays: [], balance: null });
        setUpcomingEntries([]);
        setUpcomingHolidays([]);
        setAllEntries([]);
        setBalance({ configured: false });
      } else {
        setError('Failed to load time off data');
      }
    } finally {
      setLoading(false);
    }
  };

  const getDateKey = (month, day) => {
    const m = String(month + 1).padStart(2, '0');
    const d = String(day).padStart(2, '0');
    return `${calendarYear}-${m}-${d}`;
  };

  const getDateInfo = (dateKey) => {
    if (!calendarData) return null;
    
    const entry = calendarData.entries?.find(e => {
      const start = new Date(e.start_date);
      const end = new Date(e.end_date);
      const current = new Date(dateKey);
      return current >= start && current <= end;
    });
    
    const holiday = calendarData.holidays?.find(h => h.date === dateKey);
    
    return { entry, holiday };
  };

  // Check if day is a working day based on settings
  const isWorkingDay = (month, day) => {
    const date = new Date(calendarYear, month, day);
    const dayOfWeek = date.getDay(); // 0=Sun, 1=Mon, ... 6=Sat
    
    // Default: Mon-Fri are working days
    const workingDays = settings?.working_days || [1, 2, 3, 4, 5];
    return workingDays.includes(dayOfWeek);
  };

  const isWeekend = (month, day) => {
    return !isWorkingDay(month, day);
  };

  const handleDayClick = (month, day) => {
    const dateKey = getDateKey(month, day);
    setSelectedDate(dateKey);
    setShowAddModal(true);
  };

  const renderYearView = () => {
    // Week starts on Monday, ends on Sunday (weekends on right)
    const weekDays = ['M', 'T', 'W', 'T', 'F', 'S', 'S'];
    
    return (
      <div className="year-grid">
        {MONTHS.map((monthName, monthIndex) => {
          const daysInMonth = getDaysInMonth(monthIndex, calendarYear);
          const firstDay = new Date(calendarYear, monthIndex, 1).getDay();
          // Convert Sunday=0 to Monday=0 format (Mon=0, Tue=1, ..., Sun=6)
          const startOffset = firstDay === 0 ? 6 : firstDay - 1;
          
          return (
            <div key={monthIndex} className="month-block">
              <div className="month-name">{monthName}</div>
              <div className="weekday-headers">
                {weekDays.map((d, i) => (
                  <span key={i} className={`weekday-header ${i >= 5 ? 'weekend-header' : ''}`}>{d}</span>
                ))}
              </div>
              <div className="days-grid week-layout">
                {/* Empty cells before first day */}
                {Array.from({ length: startOffset }, (_, i) => (
                  <div key={`empty-${i}`} className="day-cell empty" />
                ))}
                {/* Actual days */}
                {Array.from({ length: daysInMonth }, (_, i) => {
                  const day = i + 1;
                  const dateKey = getDateKey(monthIndex, day);
                  const info = getDateInfo(dateKey);
                  const nonWorking = !isWorkingDay(monthIndex, day);
                  
                  let className = 'day-cell';
                  let style = {};
                  let tooltip = '';
                  
                  if (nonWorking) {
                    className += ' weekend';
                  }
                  
                  // Bank holiday - highlight with special indicator
                  if (info?.holiday) {
                    className += ' bank-holiday';
                    tooltip = info.holiday.local_name || info.holiday.name;
                  }
                  
                  // Leave entry
                  if (info?.entry) {
                    className += ' leave';
                    style.backgroundColor = info.entry.color;
                    tooltip = `${info.entry.type_name}: ${info.entry.notes || ''}`;
                  }
                  
                  return (
                    <div
                      key={day}
                      className={className}
                      style={style}
                      title={tooltip}
                      onClick={() => handleDayClick(monthIndex, day)}
                    >
                      {day}
                      {info?.holiday && !info?.entry && <span className="holiday-dot" />}
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  const renderBalanceCard = () => {
    if (!balance?.configured) {
      return (
        <div className="balance-card not-configured">
          <div className="balance-icon">Settings</div>
          <div className="balance-message">
            <h3>Configure your leave settings</h3>
            <p>Set up your annual leave allowance to start tracking</p>
            <button className="btn-primary" onClick={() => setActiveTab('settings')}>
              Configure Now
            </button>
          </div>
        </div>
      );
    }

    const percentage = balance.total_days > 0 
      ? Math.round((balance.used_days / balance.total_days) * 100) 
      : 0;

    // Calculate balance period title based on employment year_type/fiscal_year_start
    const getBalancePeriodTitle = () => {
      // Use employment data if available, fallback to settings
      const yearType = currentJob?.year_type || settings?.year_type || 'calendar';
      const fiscalStart = currentJob?.fiscal_year_start || settings?.fiscal_year_start;
      
      if (yearType === 'calendar' || !fiscalStart) {
        // Calendar year: Jan - Dec
        return `Jan - Dec ${balanceYear}`;
      } else {
        // Fiscal year: use fiscal_year_start date to determine period
        const fiscalDate = new Date(fiscalStart);
        const startMonth = fiscalDate.getMonth(); // 0-indexed
        
        const startMonthName = MONTHS[startMonth];
        // End month is one month before start month
        const endMonth = startMonth === 0 ? 11 : startMonth - 1;
        const endMonthName = MONTHS[endMonth];
        
        // For fiscal year, the selected balanceYear determines start year
        const today = new Date();
        const currentYearFiscalStart = new Date(balanceYear, startMonth, fiscalDate.getDate());
        
        let fiscalYearStartYear, fiscalYearEndYear;
        if (balanceYear === currentYear) {
          // For current year, check if we're before or after fiscal start
          if (today >= currentYearFiscalStart) {
            fiscalYearStartYear = balanceYear;
            fiscalYearEndYear = balanceYear + 1;
          } else {
            fiscalYearStartYear = balanceYear - 1;
            fiscalYearEndYear = balanceYear;
          }
        } else {
          // For other years, use selected year as the start
          fiscalYearStartYear = balanceYear;
          fiscalYearEndYear = balanceYear + 1;
        }
        
        return `${startMonthName} ${fiscalYearStartYear} - ${endMonthName} ${fiscalYearEndYear}`;
      }
    };

    // Check for pro-rata (especially ending mid-year)
    const hasProrata = balance.prorata?.applies;
    const prorataReason = balance.prorata?.reason;
    const isEnding = prorataReason === 'ending_mid_year' || prorataReason === 'started_and_ending';
    const endDate = balance.prorata?.employment_end_in_year;
    const isOngoing = currentJob?.is_ongoing !== false && !currentJob?.employment_end_date;

    // Calculate days until employment ends
    const getDaysUntilEnd = () => {
      if (!endDate) return null;
      const end = new Date(endDate);
      const today = new Date();
      const diff = Math.ceil((end - today) / (1000 * 60 * 60 * 24));
      return diff;
    };
    const daysUntilEnd = getDaysUntilEnd();

    // Check if user has unused days that might be lost
    const unusedDays = balance.available_days;
    const willLoseDays = isEnding && unusedDays > 0 && daysUntilEnd !== null && daysUntilEnd < unusedDays * 2;

    return (
      <div className={`balance-card ${isEnding ? 'ending-soon' : ''} ${balanceLoading ? 'balance-loading' : ''}`}>
        <div className="balance-header">
          <div className="balance-header-left">
            <span className="balance-icon">🏖️</span>
            <span className="balance-title">Leave Balance</span>
          </div>
          <div className="balance-period-nav">
            <button 
              className="period-nav-btn" 
              onClick={() => setBalanceYear(y => y - 1)}
              title="Previous year"
            >
              ‹
            </button>
            <span className={`balance-period ${balanceYear !== currentYear ? 'not-current' : ''}`}>
              {getBalancePeriodTitle()}
            </span>
            <button 
              className="period-nav-btn" 
              onClick={() => setBalanceYear(y => y + 1)}
              title="Next year"
            >
              ›
            </button>
            {balanceYear !== currentYear && (
              <button 
                className="period-today-btn" 
                onClick={() => setBalanceYear(currentYear)}
                title="Back to current year"
              >
                Today
              </button>
            )}
          </div>
        </div>
        
        {/* Alert banner for employment ending */}
        {isEnding && endDate && (
          <div className="ending-alert">
            <span className="alert-icon">⚠️</span>
            <div className="alert-content">
              <span className="alert-title">Employment ending {new Date(endDate).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}</span>
              <span className="alert-detail">
                {daysUntilEnd !== null && daysUntilEnd > 0 
                  ? `${daysUntilEnd} days remaining · ` 
                  : ''}
                Leave calculated pro-rata ({balance.prorata?.ratio}%)
              </span>
            </div>
          </div>
        )}

        {/* Warning if user might lose unused days */}
        {willLoseDays && (
          <div className="warning-banner">
            <span className="warning-icon">💡</span>
            <span className="warning-text">You have {unusedDays} days to use before leaving!</span>
          </div>
        )}
        
        <div className="balance-main">
          <div className="balance-available">{balance.available_days}</div>
          <div className="balance-available-label">Days Available</div>
        </div>
        <div className="balance-stats">
          <div className="stat">
            <span className="stat-value">{balance.used_days}</span>
            <span className="stat-label">Used</span>
          </div>
          <div className="stat">
            <span className="stat-value">{balance.planned_days}</span>
            <span className="stat-label">Planned</span>
          </div>
          <div className="stat">
            <span className="stat-value">{balance.total_days}</span>
            <span className="stat-label">{hasProrata ? 'Pro-rata' : 'Total'}</span>
          </div>
        </div>
        
        {/* Show entitlement breakdown if pro-rata applies */}
        {hasProrata && balance.entitlement && (
          <div className="prorata-breakdown">
            <span className="breakdown-label">Full year: {balance.entitlement.total} days</span>
            <span className="breakdown-arrow">→</span>
            <span className="breakdown-value">Pro-rata: {balance.prorata.adjusted_days} days</span>
          </div>
        )}
        
        <div className="balance-progress">
          <div className="progress-bar">
            <div 
              className="progress-used" 
              style={{ width: `${percentage}%` }}
            />
            <div 
              className="progress-planned" 
              style={{ 
                width: `${balance.total_days > 0 ? (balance.planned_days / balance.total_days) * 100 : 0}%`,
                left: `${percentage}%`
              }}
            />
          </div>
          <span className="progress-text">{percentage}% used</span>
        </div>

        {/* Plan employment change button */}
        {currentJob && (
          <button 
            className="btn-employment-change"
            onClick={() => setShowEmploymentChangeModal(true)}
          >
            {isOngoing ? '📅 Plan Job Change' : '✏️ Edit End Date'}
          </button>
        )}
      </div>
    );
  };

  const renderLegend = () => {
    const items = [
      { color: '#f3f4f6', label: 'Non-working', border: true },
      { color: '#fbbf24', label: 'Bank Holiday', isDot: true },
      ...leaveTypes.map(t => ({ color: t.color, label: t.name })),
    ];

    return (
      <div className="legend">
        {items.map((item, i) => (
          <div key={i} className="legend-item">
            {item.isDot ? (
              <span className="legend-dot" style={{ backgroundColor: item.color }} />
            ) : (
              <span className="legend-color" style={{ backgroundColor: item.color, border: item.border ? '1px solid #d1d5db' : 'none' }} />
            )}
            <span className="legend-label">{item.label}</span>
          </div>
        ))}
      </div>
    );
  };

  const renderUpcoming = () => {
    const upcoming = upcomingEntries
      ?.filter(e => new Date(e.start_date) >= new Date() && e.status !== 'cancelled')
      .sort((a, b) => new Date(a.start_date) - new Date(b.start_date))
      .slice(0, 5);

    if (!upcoming?.length) {
      return (
        <div className="upcoming-empty">
          <span className="empty-icon">📅</span>
          <p>No upcoming time off scheduled</p>
          <button className="btn-outline" onClick={() => setShowAddModal(true)}>
            Plan Time Off
          </button>
        </div>
      );
    }

    return (
      <div className="upcoming-list">
        {upcoming.map(entry => (
          <div key={entry.id} className="upcoming-item" style={{ borderLeftColor: entry.color }}>
            <div className="upcoming-dates">
              {entry.start_date === entry.end_date 
                ? formatDate(entry.start_date)
                : `${formatDate(entry.start_date)} - ${formatDate(entry.end_date)}`
              }
            </div>
            <div className="upcoming-type">{entry.type_name}</div>
            <div className="upcoming-days">{entry.days_count} day{entry.days_count !== 1 ? 's' : ''}</div>
            <span className={`upcoming-status status-${entry.status}`}>{entry.status}</span>
          </div>
        ))}
      </div>
    );
  };

  // Get fiscal year boundary date for a given year
  const getFiscalYearStart = (year) => {
    const yearType = currentJob?.year_type || settings?.year_type || 'calendar';
    const fiscalStartStr = currentJob?.fiscal_year_start || settings?.fiscal_year_start;
    
    if (yearType === 'calendar' || !fiscalStartStr) {
      return new Date(year, 0, 1); // Jan 1
    } else {
      const fiscalDate = new Date(fiscalStartStr);
      return new Date(year, fiscalDate.getMonth(), fiscalDate.getDate());
    }
  };

  // Helper to determine which period a single date belongs to
  const getPeriodForDate = (date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const day = date.getDate();
    
    const yearType = currentJob?.year_type || settings?.year_type || 'calendar';
    const fiscalStartStr = currentJob?.fiscal_year_start || settings?.fiscal_year_start;
    
    if (yearType === 'calendar' || !fiscalStartStr) {
      return { type: 'calendar', label: `${year}`, sortKey: year, yearStart: year };
    } else {
      const fiscalDate = new Date(fiscalStartStr);
      const fiscalStartMonth = fiscalDate.getMonth();
      const fiscalStartDay = fiscalDate.getDate();
      
      const isAfterFiscalStart = month > fiscalStartMonth || 
        (month === fiscalStartMonth && day >= fiscalStartDay);
      
      if (isAfterFiscalStart) {
        return { type: 'fiscal', label: `Fiscal Year ${year}-${year + 1}`, sortKey: year, yearStart: year };
      } else {
        return { type: 'fiscal', label: `Fiscal Year ${year - 1}-${year}`, sortKey: year - 1, yearStart: year - 1 };
      }
    }
  };

  // Count working days between two dates (excludes weekends AND bank holidays)
  const countWorkingDays = (start, end, holidays = []) => {
    let count = 0;
    const current = new Date(start);
    const endDate = new Date(end);
    const workingDays = settings?.working_days || [1, 2, 3, 4, 5];
    
    // Create a Set of holiday date strings for fast lookup
    const holidaySet = new Set(holidays.map(h => h.date));
    
    while (current <= endDate) {
      const dateStr = current.toISOString().split('T')[0];
      const isWeekend = !workingDays.includes(current.getDay());
      const isHoliday = holidaySet.has(dateStr);
      
      if (!isWeekend && !isHoliday) {
        count++;
      }
      current.setDate(current.getDate() + 1);
    }
    return count;
  };

  // Split entry into periods with date ranges and day counts for each
  // Uses upcomingHolidays from state to exclude bank holidays from count
  const splitEntryByPeriods = (entry) => {
    const startDate = new Date(entry.start_date);
    const endDate = new Date(entry.end_date);
    
    const startPeriod = getPeriodForDate(startDate);
    const endPeriod = getPeriodForDate(endDate);
    
    // Get all holidays (combine from calendar data)
    const allHolidays = [
      ...(calendarData?.holidays || []),
      ...(upcomingHolidays || [])
    ].filter((h, i, arr) => arr.findIndex(x => x.date === h.date) === i); // dedupe
    
    // If same period, return single entry
    if (startPeriod.label === endPeriod.label) {
      return [{
        ...entry,
        periodLabel: startPeriod.label,
        period: startPeriod,
        displayStartDate: entry.start_date,
        displayEndDate: entry.end_date,
        periodDays: entry.days_count, // Use backend-calculated days
        spansPeriods: false
      }];
    }
    
    // Entry spans periods - split at boundary
    const yearType = currentJob?.year_type || settings?.year_type || 'calendar';
    const fiscalStartStr = currentJob?.fiscal_year_start || settings?.fiscal_year_start;
    
    // Find the boundary date (start of the new period)
    let boundaryDate;
    if (yearType === 'calendar' || !fiscalStartStr) {
      // Calendar year boundary is Jan 1 of end year
      boundaryDate = new Date(endDate.getFullYear(), 0, 1);
    } else {
      // Fiscal year boundary
      const fiscalDate = new Date(fiscalStartStr);
      const fiscalMonth = fiscalDate.getMonth();
      const fiscalDay = fiscalDate.getDate();
      
      // Find the fiscal year start that falls between start and end dates
      let testYear = startDate.getFullYear();
      boundaryDate = new Date(testYear, fiscalMonth, fiscalDay);
      
      // If boundary is before or equal to start, try next year
      if (boundaryDate <= startDate) {
        testYear++;
        boundaryDate = new Date(testYear, fiscalMonth, fiscalDay);
      }
    }
    
    // Day before boundary (last day of first period)
    const dayBeforeBoundary = new Date(boundaryDate);
    dayBeforeBoundary.setDate(dayBeforeBoundary.getDate() - 1);
    
    // Calculate days for each portion (excluding weekends and bank holidays)
    const firstPeriodDays = countWorkingDays(startDate, dayBeforeBoundary, allHolidays);
    const secondPeriodDays = countWorkingDays(boundaryDate, endDate, allHolidays);
    
    // Format dates as strings
    const formatDateStr = (d) => d.toISOString().split('T')[0];
    
    return [
      {
        ...entry,
        periodLabel: startPeriod.label,
        period: startPeriod,
        displayStartDate: entry.start_date,
        displayEndDate: formatDateStr(dayBeforeBoundary),
        periodDays: firstPeriodDays,
        spansPeriods: true,
        allPeriods: [startPeriod.label, endPeriod.label]
      },
      {
        ...entry,
        periodLabel: endPeriod.label,
        period: endPeriod,
        displayStartDate: formatDateStr(boundaryDate),
        displayEndDate: entry.end_date,
        periodDays: secondPeriodDays,
        spansPeriods: true,
        allPeriods: [startPeriod.label, endPeriod.label]
      }
    ];
  };
  
  // Legacy function for compatibility
  const getEntryPeriod = (entry) => getPeriodForDate(new Date(entry.start_date));

  // State for entries view filters
  const [entriesFilter, setEntriesFilter] = useState('all'); // all, upcoming, past

  const renderAllTimeOff = () => {
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    
    // Calculate stats
    const allValid = allEntries?.filter(e => e.status !== 'cancelled') || [];
    const stats = {
      total: allValid.length,
      upcoming: allValid.filter(e => new Date(e.start_date) >= today).length,
      totalDays: allValid.reduce((sum, e) => sum + (e.days_count || 0), 0),
    };

    // Filter entries
    let filteredEntries = [...allValid];
    
    if (entriesFilter === 'upcoming') {
      filteredEntries = filteredEntries.filter(e => new Date(e.start_date) >= today);
    } else if (entriesFilter === 'past') {
      filteredEntries = filteredEntries.filter(e => new Date(e.end_date) < today);
    }

    // Group by period - split entries that cross period boundaries
    const grouped = {};
    filteredEntries.forEach(entry => {
      const splitEntries = splitEntryByPeriods(entry);
      
      splitEntries.forEach(splitEntry => {
        const periodLabel = splitEntry.periodLabel;
        if (!grouped[periodLabel]) {
          grouped[periodLabel] = { ...splitEntry.period, entries: [] };
        }
        grouped[periodLabel].entries.push(splitEntry);
      });
    });

    // Sort periods (most recent first)
    const sortedPeriods = Object.values(grouped).sort((a, b) => b.sortKey - a.sortKey);

    // Format date range nicely
    const formatDateRange = (start, end, showYear = false) => {
      const startDate = new Date(start);
      const endDate = new Date(end);
      const startDay = startDate.getDate();
      const endDay = endDate.getDate();
      const startMonth = startDate.toLocaleDateString('en-GB', { month: 'short' });
      const endMonth = endDate.toLocaleDateString('en-GB', { month: 'short' });
      const startYear = startDate.getFullYear();
      const endYear = endDate.getFullYear();
      
      if (start === end) {
        return showYear ? `${startDay} ${startMonth} ${startYear}` : `${startDay} ${startMonth}`;
      }
      if (startMonth === endMonth && startYear === endYear) {
        return showYear ? `${startDay}-${endDay} ${startMonth} ${startYear}` : `${startDay}-${endDay} ${startMonth}`;
      }
      if (startYear === endYear) {
        return showYear ? `${startDay} ${startMonth} - ${endDay} ${endMonth} ${endYear}` : `${startDay} ${startMonth} - ${endDay} ${endMonth}`;
      }
      return `${startDay} ${startMonth} ${startYear} - ${endDay} ${endMonth} ${endYear}`;
    };

    // Get entry visual state (based on display dates for this period)
    const getEntryState = (entry) => {
      const startDate = new Date(entry.displayStartDate);
      const endDate = new Date(entry.displayEndDate);
      
      if (endDate < today) return 'past';
      if (startDate <= today && endDate >= today) return 'ongoing';
      return 'upcoming';
    };

    if (!allEntries?.length) {
      return (
        <div className="entries-view-v2">
          <div className="entries-empty">
            <div className="empty-icon">🏖️</div>
            <h3>No time off yet</h3>
            <p>Start planning your well-deserved breaks</p>
            <button className="btn-primary" onClick={() => setShowAddModal(true)}>
              + Request Time Off
            </button>
          </div>
        </div>
      );
    }

    return (
      <div className="entries-view-v2">
        {/* Filters Row */}
        <div className="entries-header">
          <div className="filter-tabs">
            <button 
              className={`filter-tab ${entriesFilter === 'all' ? 'active' : ''}`}
              onClick={() => setEntriesFilter('all')}
            >
              All <span className="tab-count">{stats.total}</span>
            </button>
            <button 
              className={`filter-tab ${entriesFilter === 'upcoming' ? 'active' : ''}`}
              onClick={() => setEntriesFilter('upcoming')}
            >
              Upcoming <span className="tab-count">{stats.upcoming}</span>
            </button>
            <button 
              className={`filter-tab ${entriesFilter === 'past' ? 'active' : ''}`}
              onClick={() => setEntriesFilter('past')}
            >
              Past <span className="tab-count">{stats.total - stats.upcoming}</span>
            </button>
          </div>
          <div className="entries-summary">
            {filteredEntries.length} {filteredEntries.length === 1 ? 'entry' : 'entries'} · {filteredEntries.reduce((s, e) => s + (e.days_count || 0), 0)} days
          </div>
        </div>

        {/* Entries grouped by period */}
        {filteredEntries.length === 0 ? (
          <div className="entries-empty-filter">
            <p>No {entriesFilter} entries</p>
          </div>
        ) : (
          <div className="entries-periods">
            {sortedPeriods.map(period => (
              <div key={period.label} className="period-section">
                {/* Period Header */}
                <div className="period-header">
                  <span className="period-icon">{period.type === 'fiscal' ? '📊' : '📆'}</span>
                  <span className="period-title">{period.label}</span>
                  <span className="period-stats">
                    {period.entries.length} {period.entries.length === 1 ? 'entry' : 'entries'} · {period.entries.reduce((s, e) => s + (e.periodDays || 0), 0)} days
                  </span>
                </div>
                
                {/* Period Entries */}
                <div className="period-entries">
                  {period.entries
                    .sort((a, b) => new Date(b.displayStartDate) - new Date(a.displayStartDate))
                    .map(entry => {
                      const state = getEntryState(entry);
                      
                      return (
                        <div 
                          key={entry.id} 
                          className={`entry-row ${state}`}
                          onClick={() => setEditingEntry(entry)}
                        >
                          <div className="entry-color" style={{ backgroundColor: entry.color }} />
                          
                          <div className="entry-date-block">
                            <div className="entry-date-row">
                              <span className="entry-date">{formatDateRange(entry.displayStartDate, entry.displayEndDate)}</span>
                              {entry.spansPeriods && (
                                <span className="spans-badge" title={`Full: ${formatDateRange(entry.start_date, entry.end_date)} (${entry.days_count} days total)`}>↔</span>
                              )}
                            </div>
                            {state === 'ongoing' && <span className="now-badge">NOW</span>}
                          </div>
                          
                          <div className="entry-info">
                            <span className="entry-type" style={{ color: entry.color }}>{entry.type_name}</span>
                            {entry.notes && <span className="entry-notes">— {entry.notes}</span>}
                          </div>
                          
                          <div className="entry-days">
                            <span className="days-num">{entry.periodDays}</span>
                            <span className="days-label">{entry.periodDays === 1 ? 'day' : 'days'}</span>
                          </div>
                          
                          <div className={`entry-status status-${entry.status}`}>
                            {entry.status}
                          </div>
                          
                          <div className="entry-arrow">›</div>
                        </div>
                      );
                    })}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    );
  };

  // Render upcoming bank holidays
  const renderUpcomingHolidays = () => {
    const today = new Date();
    const upcoming = upcomingHolidays
      ?.filter(h => new Date(h.date) >= today)
      .sort((a, b) => new Date(a.date) - new Date(b.date))
      .slice(0, 5);

    if (!upcoming?.length) return null;

    return (
      <div className="sidebar-section">
        <h3>Upcoming Holidays</h3>
        <div className="holidays-list">
          {upcoming.map((holiday, idx) => (
            <div key={idx} className="holiday-item">
              <span className="holiday-date">{formatDate(holiday.date)}</span>
              <span className="holiday-name">{holiday.local_name || holiday.name}</span>
            </div>
          ))}
        </div>
      </div>
    );
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
  };

  if (loading) {
    return (
      <div className="timeoff-page">
        <div className="loading-container">
          <div className="loading-spinner" />
          <p>Loading time off data...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="timeoff-page">
      <header className="timeoff-header">
        <div className="header-left">
          <h1>Time Off</h1>
        </div>
        <div className="header-right">
          <button className="btn-primary" onClick={() => setShowAddModal(true)}>
            + Request Time Off
          </button>
        </div>
      </header>

      <div className="timeoff-tabs">
        <button 
          className={`tab ${activeTab === 'calendar' ? 'active' : ''}`}
          onClick={() => setActiveTab('calendar')}
        >
          Calendar
        </button>
        <button 
          className={`tab ${activeTab === 'entries' ? 'active' : ''}`}
          onClick={() => setActiveTab('entries')}
        >
          My Time Off
        </button>
        <button 
          className={`tab ${activeTab === 'settings' ? 'active' : ''}`}
          onClick={() => setActiveTab('settings')}
        >
          Settings
        </button>
      </div>

      {error && (
        <div className="error-banner">
          {error}
          <button onClick={fetchData}>Retry</button>
        </div>
      )}

      {activeTab === 'calendar' && (
        <div className="calendar-view">
          <div className="calendar-sidebar">
            {renderBalanceCard()}
            <div className="sidebar-section">
              <h3>Upcoming Time Off</h3>
              {renderUpcoming()}
            </div>
            {renderUpcomingHolidays()}
            {renderLegend()}
          </div>
          <div className="calendar-main">
            <div className="calendar-year-nav">
              <button onClick={() => setCalendarYear(y => y - 1)}>&lt;</button>
              <span className="current-year">{calendarYear}</span>
              <button onClick={() => setCalendarYear(y => y + 1)}>&gt;</button>
            </div>
            {renderYearView()}
          </div>
        </div>
      )}

      {activeTab === 'entries' && (
        <div className="entries-view">
          {renderAllTimeOff()}
        </div>
      )}

      {activeTab === 'settings' && (
        <TimeOffSettings 
          year={currentYear}
          leaveTypes={leaveTypes}
          countries={countries}
          currentSettings={settings}
          onRefresh={fetchData}
        />
      )}

      {showAddModal && (
        <AddTimeOffModal
          initialDate={selectedDate}
          leaveTypes={leaveTypes}
          settings={settings}
          holidays={calendarData?.holidays || []}
          onClose={() => { setShowAddModal(false); setSelectedDate(null); }}
          onSuccess={() => { setShowAddModal(false); setSelectedDate(null); fetchData(); }}
        />
      )}

      {editingEntry && (
        <EditEntryModal
          entry={editingEntry}
          leaveTypes={leaveTypes}
          settings={settings}
          holidays={calendarData?.holidays || []}
          onClose={() => setEditingEntry(null)}
          onSuccess={() => { setEditingEntry(null); fetchData(); }}
        />
      )}

      {showEmploymentChangeModal && currentJob && (
        <EmploymentChangeModal
          job={currentJob}
          balance={balance}
          onClose={() => setShowEmploymentChangeModal(false)}
          onSuccess={() => { setShowEmploymentChangeModal(false); fetchData(); }}
        />
      )}
    </div>
  );
}

// Entries List Component
function EntriesList({ entries, onRefresh }) {
  const [filter, setFilter] = useState('all');
  
  const filtered = entries.filter(e => {
    if (filter === 'all') return true;
    return e.status === filter;
  });

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this time off entry?')) return;
    try {
      await api.delete(`/timeoff/entries/${id}`);
      onRefresh();
    } catch (err) {
      alert('Failed to delete entry');
    }
  };

  return (
    <div className="entries-list-container">
      <div className="entries-filter">
        <select value={filter} onChange={(e) => setFilter(e.target.value)}>
          <option value="all">All</option>
          <option value="planned">Planned</option>
          <option value="confirmed">Confirmed</option>
          <option value="taken">Taken</option>
          <option value="cancelled">Cancelled</option>
        </select>
      </div>
      
      {filtered.length === 0 ? (
        <div className="no-entries">
          <p>No entries found</p>
        </div>
      ) : (
        <table className="entries-table">
          <thead>
            <tr>
              <th>Type</th>
              <th>Dates</th>
              <th>Days</th>
              <th>Status</th>
              <th>Notes</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map(entry => (
              <tr key={entry.id}>
                <td>
                  <span className="type-badge" style={{ backgroundColor: entry.leave_type_color }}>
                    {entry.leave_type_name}
                  </span>
                </td>
                <td>{entry.start_date} - {entry.end_date}</td>
                <td>{entry.days_count}</td>
                <td><span className={`status-badge status-${entry.status}`}>{entry.status}</span></td>
                <td>{entry.notes || '-'}</td>
                <td>
                  <button className="btn-icon" onClick={() => handleDelete(entry.id)}>Delete</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

// Country list for dropdown
const COUNTRY_LIST = [
  { code: 'GB', name: 'United Kingdom', flag: 'GB' },
  { code: 'ES', name: 'Spain', flag: 'ES' },
  { code: 'US', name: 'United States', flag: 'US' },
  { code: 'DE', name: 'Germany', flag: 'DE' },
  { code: 'FR', name: 'France', flag: 'FR' },
  { code: 'IT', name: 'Italy', flag: 'IT' },
  { code: 'PT', name: 'Portugal', flag: 'PT' },
  { code: 'NL', name: 'Netherlands', flag: 'NL' },
  { code: 'BE', name: 'Belgium', flag: 'BE' },
  { code: 'IE', name: 'Ireland', flag: 'IE' },
  { code: 'AT', name: 'Austria', flag: 'AT' },
  { code: 'CH', name: 'Switzerland', flag: 'CH' },
  { code: 'SE', name: 'Sweden', flag: 'SE' },
  { code: 'NO', name: 'Norway', flag: 'NO' },
  { code: 'DK', name: 'Denmark', flag: 'DK' },
  { code: 'FI', name: 'Finland', flag: 'FI' },
  { code: 'PL', name: 'Poland', flag: 'PL' },
  { code: 'CZ', name: 'Czech Republic', flag: 'CZ' },
  { code: 'GR', name: 'Greece', flag: 'GR' },
  { code: 'AU', name: 'Australia', flag: 'AU' },
  { code: 'CA', name: 'Canada', flag: 'CA' },
  { code: 'JP', name: 'Japan', flag: 'JP' },
  { code: 'KR', name: 'South Korea', flag: 'KR' },
  { code: 'CN', name: 'China', flag: 'CN' },
  { code: 'IN', name: 'India', flag: 'IN' },
  { code: 'BR', name: 'Brazil', flag: 'BR' },
  { code: 'MX', name: 'Mexico', flag: 'MX' },
  { code: 'AR', name: 'Argentina', flag: 'AR' },
  { code: 'ZA', name: 'South Africa', flag: 'ZA' },
  { code: 'AE', name: 'United Arab Emirates', flag: 'AE' },
  { code: 'SG', name: 'Singapore', flag: 'SG' },
  { code: 'HK', name: 'Hong Kong', flag: 'HK' },
  { code: 'NZ', name: 'New Zealand', flag: 'NZ' },
  { code: 'RU', name: 'Russia', flag: 'RU' },
  { code: 'TR', name: 'Turkey', flag: 'TR' },
];

// Settings Component
function TimeOffSettings({ year, leaveTypes, countries, currentSettings, onRefresh }) {
  const [settings, setSettings] = useState({
    base_days: 25,
    carried_over: 0,
    extra_days: 0,
    year_type: 'calendar',
    working_days: [1, 2, 3, 4, 5],
    exclude_bank_holidays: true
  });
  const [saving, setSaving] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [editingLeaveType, setEditingLeaveType] = useState(null);
  const [showCreateLeaveType, setShowCreateLeaveType] = useState(false);

  const handleDeleteLeaveType = async (leaveType) => {
    if (!window.confirm(`Delete "${leaveType.name}"? This cannot be undone.`)) {
      return;
    }
    try {
      await api.delete(`/timeoff/types/${leaveType.id}`);
      onRefresh();
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed to delete leave type');
    }
  };

  useEffect(() => {
    if (currentSettings) {
      setSettings({
        base_days: currentSettings.base_days || 25,
        carried_over: currentSettings.carried_over || 0,
        extra_days: currentSettings.extra_days || 0,
        year_type: currentSettings.year_type || 'calendar',
        working_days: currentSettings.working_days || [1, 2, 3, 4, 5],
        exclude_bank_holidays: currentSettings.exclude_bank_holidays !== false
      });
    }
  }, [currentSettings, year]);

  const saveSettings = async () => {
    setSaving(true);
    try {
      await api.post('/timeoff/settings', { ...settings, year });
      onRefresh();
    } catch (err) {
      alert('Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const addCountry = async (countryCode) => {
    if (!countryCode) return;
    if (countries.some(c => c.country_code === countryCode)) {
      alert('Country already added');
      return;
    }
    try {
      await api.post('/timeoff/countries', { 
        country_code: countryCode,
        is_primary: countries.length === 0
      });
      setSearchTerm('');
      setShowDropdown(false);
      onRefresh();
    } catch (err) {
      alert('Failed to add country');
    }
  };

  const removeCountry = async (id) => {
    try {
      await api.delete(`/timeoff/countries/${id}`);
      onRefresh();
    } catch (err) {
      alert('Failed to remove country');
    }
  };

  const availableCountries = COUNTRY_LIST.filter(c => 
    !countries.some(added => added.country_code === c.code) &&
    (c.name.toLowerCase().includes(searchTerm.toLowerCase()) || 
     c.code.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  const getCountryInfo = (code) => COUNTRY_LIST.find(c => c.code === code);

  return (
    <div className="settings-container">
      <HolidayCustomization 
        year={year} 
        countries={countries} 
        onRefresh={onRefresh}
        onCountryAdd={addCountry}
        onCountryRemove={removeCountry}
      />

      <div className="settings-section">
        <div className="section-header">
          <h3>Leave Types</h3>
          <button 
            className="btn-add-small"
            onClick={() => setShowCreateLeaveType(true)}
          >+ Add New</button>
        </div>
        <div className="leave-types-list">
          {leaveTypes.map(t => (
            <div key={t.id} className="leave-type-item">
              <span className="type-color" style={{ backgroundColor: t.color }} />
              <span className="type-name">{t.name}</span>
              {t.deducts_from_balance && <span className="deducts-badge">Deducts</span>}
              {t.is_system && <span className="system-badge">System</span>}
              <div className="type-actions">
                <button 
                  className="edit-type-btn"
                  onClick={() => setEditingLeaveType(t)}
                  title="Edit"
                >Edit</button>
                {!t.is_system && (
                  <button 
                    className="delete-type-btn"
                    onClick={() => handleDeleteLeaveType(t)}
                    title="Delete"
                  >Delete</button>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>

      {showCreateLeaveType && (
        <CreateLeaveTypeModal
          onClose={() => setShowCreateLeaveType(false)}
          onSuccess={() => {
            setShowCreateLeaveType(false);
            onRefresh();
          }}
        />
      )}

      {editingLeaveType && (
        <EditLeaveTypeModal
          leaveType={editingLeaveType}
          onClose={() => setEditingLeaveType(null)}
          onSuccess={() => {
            setEditingLeaveType(null);
            onRefresh();
          }}
        />
      )}

    </div>
  );
}

// Add Time Off Modal
function AddTimeOffModal({ initialDate, leaveTypes, settings, holidays, onClose, onSuccess }) {
  const [formData, setFormData] = useState({
    leave_type_id: leaveTypes[0]?.id || '',
    start_date: initialDate || new Date().toISOString().split('T')[0],
    end_date: initialDate || new Date().toISOString().split('T')[0],
    status: 'planned',
    notes: ''
  });
  const [saving, setSaving] = useState(false);
  const [workingDaysCount, setWorkingDaysCount] = useState(0);

  const workingDays = settings?.working_days || [1, 2, 3, 4, 5];
  const excludeHolidays = settings?.exclude_bank_holidays !== false;

  // Calculate working days between two dates
  useEffect(() => {
    const start = new Date(formData.start_date);
    const end = new Date(formData.end_date);
    let count = 0;
    
    const holidayDates = new Set(holidays.map(h => h.date));
    
    for (let d = new Date(start); d <= end; d.setDate(d.getDate() + 1)) {
      const dayOfWeek = d.getDay();
      const dateStr = d.toISOString().split('T')[0];
      
      if (workingDays.includes(dayOfWeek)) {
        if (!excludeHolidays || !holidayDates.has(dateStr)) {
          count++;
        }
      }
    }
    
    setWorkingDaysCount(count);
  }, [formData.start_date, formData.end_date, workingDays, excludeHolidays, holidays]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.post('/timeoff/entries', {
        ...formData,
        days_count: workingDaysCount
      });
      onSuccess();
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed to create entry');
    } finally {
      setSaving(false);
    }
  };

  const getTotalDays = () => {
    const start = new Date(formData.start_date);
    const end = new Date(formData.end_date);
    return Math.ceil((end - start) / (1000 * 60 * 60 * 24)) + 1;
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>Request Time Off</h2>
          <button className="modal-close" onClick={onClose}>x</button>
        </div>
        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label>Leave Type</label>
            <select
              value={formData.leave_type_id}
              onChange={(e) => setFormData({ ...formData, leave_type_id: parseInt(e.target.value) })}
              required
            >
              {leaveTypes.map(t => (
                <option key={t.id} value={t.id}>{t.name}</option>
              ))}
            </select>
          </div>
          <div className="form-row">
            <div className="form-group">
              <label>Start Date</label>
              <input
                type="date"
                value={formData.start_date}
                onChange={(e) => setFormData({ ...formData, start_date: e.target.value })}
                required
              />
            </div>
            <div className="form-group">
              <label>End Date</label>
              <input
                type="date"
                value={formData.end_date}
                onChange={(e) => setFormData({ ...formData, end_date: e.target.value })}
                min={formData.start_date}
                required
              />
            </div>
          </div>

          <div className="days-calculation">
            <div className="calc-row">
              <span className="calc-label">Calendar days:</span>
              <span className="calc-value">{getTotalDays()}</span>
            </div>
            <div className="calc-row highlight">
              <span className="calc-label">Working days (leave used):</span>
              <span className="calc-value">{workingDaysCount}</span>
            </div>
            {excludeHolidays && (
              <p className="calc-note">* Excludes non-working days and bank holidays</p>
            )}
          </div>

          <div className="form-group">
            <label>Status</label>
            <select
              value={formData.status}
              onChange={(e) => setFormData({ ...formData, status: e.target.value })}
            >
              <option value="planned">Planned</option>
              <option value="confirmed">Confirmed</option>
            </select>
          </div>
          <div className="form-group">
            <label>Notes (optional)</label>
            <textarea
              value={formData.notes}
              onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
              placeholder="Add any notes..."
              rows={3}
            />
          </div>
          <div className="modal-actions">
            <button type="button" className="btn-secondary" onClick={onClose}>Cancel</button>
            <button type="submit" className="btn-primary" disabled={saving || workingDaysCount === 0}>
              {saving ? 'Saving...' : `Request ${workingDaysCount} Day${workingDaysCount !== 1 ? 's' : ''} Off`}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// Edit Entry Modal - with working days recalculation
function EditEntryModal({ entry, leaveTypes, settings, holidays = [], onClose, onSuccess }) {
  const [formData, setFormData] = useState({
    leave_type_id: entry.leave_type_id,
    start_date: entry.start_date,
    end_date: entry.end_date,
    status: entry.status,
    notes: entry.notes || ''
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [workingDaysCount, setWorkingDaysCount] = useState(entry.days_count || 0);

  const workingDays = settings?.working_days || [1, 2, 3, 4, 5];
  const excludeHolidays = settings?.exclude_bank_holidays !== false;

  // Recalculate working days when dates change
  useEffect(() => {
    const start = new Date(formData.start_date);
    const end = new Date(formData.end_date);
    let count = 0;
    
    const holidayDates = new Set(holidays.map(h => h.date));
    
    for (let d = new Date(start); d <= end; d.setDate(d.getDate() + 1)) {
      const dayOfWeek = d.getDay();
      const dateStr = d.toISOString().split('T')[0];
      
      if (workingDays.includes(dayOfWeek)) {
        if (!excludeHolidays || !holidayDates.has(dateStr)) {
          count++;
        }
      }
    }
    
    setWorkingDaysCount(count);
  }, [formData.start_date, formData.end_date, workingDays, excludeHolidays, holidays]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError(null);
    try {
      await api.put(`/timeoff/entries/${entry.id}`, {
        ...formData,
        days_count: workingDaysCount
      });
      onSuccess();
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to update entry');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm('Delete this time off entry? This cannot be undone.')) return;
    try {
      await api.delete(`/timeoff/entries/${entry.id}`);
      onSuccess();
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to delete entry');
    }
  };

  const getTotalDays = () => {
    const start = new Date(formData.start_date);
    const end = new Date(formData.end_date);
    return Math.ceil((end - start) / (1000 * 60 * 60 * 24)) + 1;
  };

  // Check if dates have changed from original
  const datesChanged = formData.start_date !== entry.start_date || formData.end_date !== entry.end_date;

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h2>Edit Time Off</h2>
          <button className="modal-close" onClick={onClose}>×</button>
        </div>
        {error && <div className="error-message">{error}</div>}
        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label>Type</label>
            <select
              value={formData.leave_type_id}
              onChange={(e) => setFormData({ ...formData, leave_type_id: parseInt(e.target.value) })}
            >
              {leaveTypes.map(t => (
                <option key={t.id} value={t.id}>{t.name}</option>
              ))}
            </select>
          </div>
          <div className="form-row">
            <div className="form-group">
              <label>Start Date</label>
              <input
                type="date"
                value={formData.start_date}
                onChange={(e) => setFormData({ ...formData, start_date: e.target.value })}
                required
              />
            </div>
            <div className="form-group">
              <label>End Date</label>
              <input
                type="date"
                value={formData.end_date}
                onChange={(e) => setFormData({ ...formData, end_date: e.target.value })}
                min={formData.start_date}
                required
              />
            </div>
          </div>

          <div className="days-calculation">
            <div className="calc-row">
              <span className="calc-label">Calendar days:</span>
              <span className="calc-value">{getTotalDays()}</span>
            </div>
            <div className="calc-row highlight">
              <span className="calc-label">Working days (leave used):</span>
              <span className="calc-value">
                {workingDaysCount}
                {datesChanged && entry.days_count !== workingDaysCount && (
                  <span className="days-change">
                    (was {entry.days_count})
                  </span>
                )}
              </span>
            </div>
            {excludeHolidays && (
              <p className="calc-note">* Excludes non-working days and bank holidays</p>
            )}
          </div>

          <div className="form-group">
            <label>Status</label>
            <select
              value={formData.status}
              onChange={(e) => setFormData({ ...formData, status: e.target.value })}
            >
              <option value="planned">Planned</option>
              <option value="confirmed">Confirmed</option>
              <option value="taken">Taken</option>
              <option value="cancelled">Cancelled</option>
            </select>
          </div>
          <div className="form-group">
            <label>Notes</label>
            <textarea
              value={formData.notes}
              onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
              placeholder="Add any notes..."
              rows={3}
            />
          </div>
          <div className="modal-actions">
            <button type="button" className="btn-danger" onClick={handleDelete}>Delete</button>
            <div className="action-right">
              <button type="button" className="btn-secondary" onClick={onClose}>Cancel</button>
              <button type="submit" className="btn-primary" disabled={saving || workingDaysCount === 0}>
                {saving ? 'Saving...' : `Save (${workingDaysCount} day${workingDaysCount !== 1 ? 's' : ''})`}
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
}

// Edit Leave Type Modal
function EditLeaveTypeModal({ leaveType, onClose, onSuccess }) {
  const [name, setName] = useState(leaveType.name);
  const [color, setColor] = useState(leaveType.color);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  // Available colors (excluding bank holiday yellow and weekend grays)
  const AVAILABLE_COLORS = [
    { hex: '#22c55e', name: 'Green' },
    { hex: '#10b981', name: 'Emerald' },
    { hex: '#14b8a6', name: 'Teal' },
    { hex: '#06b6d4', name: 'Cyan' },
    { hex: '#3b82f6', name: 'Blue' },
    { hex: '#6366f1', name: 'Indigo' },
    { hex: '#8b5cf6', name: 'Violet' },
    { hex: '#a855f7', name: 'Purple' },
    { hex: '#d946ef', name: 'Fuchsia' },
    { hex: '#ec4899', name: 'Pink' },
    { hex: '#f43f5e', name: 'Rose' },
    { hex: '#ef4444', name: 'Red' },
    { hex: '#f97316', name: 'Orange' },
  ];

  // Blocked colors (bank holidays = yellow/amber, weekends = gray)
  const BLOCKED_COLORS = [
    { hex: '#fbbf24', name: 'Bank Holiday Yellow', reason: 'Reserved for bank holidays' },
    { hex: '#f59e0b', name: 'Amber', reason: 'Reserved for bank holidays' },
    { hex: '#eab308', name: 'Yellow', reason: 'Reserved for bank holidays' },
    { hex: '#e5e7eb', name: 'Light Gray', reason: 'Reserved for weekends' },
    { hex: '#d1d5db', name: 'Gray', reason: 'Reserved for weekends' },
    { hex: '#9ca3af', name: 'Dark Gray', reason: 'Reserved for weekends' },
  ];

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!name.trim()) {
      setError('Name is required');
      return;
    }

    setSaving(true);
    setError(null);

    try {
      await api.put(`/timeoff/types/${leaveType.id}`, {
        name: name.trim(),
        color
      });
      onSuccess();
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to update leave type');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        <h2>Edit Leave Type</h2>
        
        {error && <div className="error-message">{error}</div>}

        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label>Name</label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g., Vacation, Sick Leave"
              required
            />
          </div>

          <div className="form-group">
            <label>Color</label>
            <div className="color-picker-grid">
              {AVAILABLE_COLORS.map((c) => (
                <button
                  key={c.hex}
                  type="button"
                  className={`color-option ${color === c.hex ? 'selected' : ''}`}
                  style={{ backgroundColor: c.hex }}
                  onClick={() => setColor(c.hex)}
                  title={c.name}
                />
              ))}
            </div>
          </div>

          <div className="form-group">
            <label className="blocked-colors-label">Blocked Colors</label>
            <div className="blocked-colors-info">
              {BLOCKED_COLORS.map((c) => (
                <div key={c.hex} className="blocked-color-item">
                  <span className="blocked-color-swatch" style={{ backgroundColor: c.hex }} />
                  <span className="blocked-color-reason">{c.reason}</span>
                </div>
              ))}
            </div>
          </div>

          <div className="modal-actions">
            <button type="button" className="btn-secondary" onClick={onClose}>
              Cancel
            </button>
            <button type="submit" className="btn-primary" disabled={saving}>
              {saving ? 'Saving...' : 'Save Changes'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// Create Leave Type Modal
function CreateLeaveTypeModal({ onClose, onSuccess }) {
  const [name, setName] = useState('');
  const [color, setColor] = useState('#3b82f6');
  const [deductsFromBalance, setDeductsFromBalance] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const AVAILABLE_COLORS = [
    { hex: '#22c55e', name: 'Green' },
    { hex: '#10b981', name: 'Emerald' },
    { hex: '#14b8a6', name: 'Teal' },
    { hex: '#06b6d4', name: 'Cyan' },
    { hex: '#3b82f6', name: 'Blue' },
    { hex: '#6366f1', name: 'Indigo' },
    { hex: '#8b5cf6', name: 'Violet' },
    { hex: '#a855f7', name: 'Purple' },
    { hex: '#d946ef', name: 'Fuchsia' },
    { hex: '#ec4899', name: 'Pink' },
    { hex: '#f43f5e', name: 'Rose' },
    { hex: '#ef4444', name: 'Red' },
    { hex: '#f97316', name: 'Orange' },
  ];

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!name.trim()) {
      setError('Name is required');
      return;
    }

    setSaving(true);
    setError(null);

    try {
      await api.post('/timeoff/types', {
        name: name.trim(),
        color,
        deducts_from_balance: deductsFromBalance
      });
      onSuccess();
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to create leave type');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        <h2>Create Leave Type</h2>
        
        {error && <div className="error-message">{error}</div>}

        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label>Name</label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g., Vacation, Sick Leave, Personal"
              required
            />
          </div>

          <div className="form-group">
            <label>Color</label>
            <div className="color-picker-grid">
              {AVAILABLE_COLORS.map((c) => (
                <button
                  key={c.hex}
                  type="button"
                  className={`color-option ${color === c.hex ? 'selected' : ''}`}
                  style={{ backgroundColor: c.hex }}
                  onClick={() => setColor(c.hex)}
                  title={c.name}
                />
              ))}
            </div>
          </div>

          <div className="form-group">
            <label className="checkbox-label">
              <input
                type="checkbox"
                checked={deductsFromBalance}
                onChange={(e) => setDeductsFromBalance(e.target.checked)}
              />
              <span>Deducts from leave balance</span>
            </label>
            <p className="field-hint">
              Uncheck for leave types like sick leave that don't count against your annual allowance.
            </p>
          </div>

          <div className="modal-actions">
            <button type="button" className="btn-secondary" onClick={onClose}>
              Cancel
            </button>
            <button type="submit" className="btn-primary" disabled={saving}>
              {saving ? 'Creating...' : 'Create Leave Type'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// Employment Change Modal - Plan job end date
function EmploymentChangeModal({ job, balance, onClose, onSuccess }) {
  const [endDate, setEndDate] = useState(job.employment_end_date || '');
  const [isOngoing, setIsOngoing] = useState(job.is_ongoing !== false && !job.employment_end_date);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [previewProrata, setPreviewProrata] = useState(null);

  // Calculate the fiscal year boundaries based on job settings
  const getFiscalYearBoundaries = () => {
    const today = new Date();
    const currentCalendarYear = today.getFullYear();
    
    // Check if using fiscal year or calendar year
    const yearType = job.year_type || 'calendar';
    const fiscalStartStr = job.fiscal_year_start;
    
    if (yearType === 'calendar' || !fiscalStartStr) {
      // Calendar year: Jan 1 - Dec 31
      return {
        yearStart: new Date(currentCalendarYear, 0, 1),
        yearEnd: new Date(currentCalendarYear, 11, 31)
      };
    } else {
      // Fiscal year: use fiscal_year_start to determine boundaries
      const fiscalDate = new Date(fiscalStartStr);
      const fiscalStartMonth = fiscalDate.getMonth(); // 0-indexed
      const fiscalStartDay = fiscalDate.getDate();
      
      // Determine which fiscal year we're currently in
      const currentYearFiscalStart = new Date(currentCalendarYear, fiscalStartMonth, fiscalStartDay);
      
      let fiscalYearStart, fiscalYearEnd;
      if (today >= currentYearFiscalStart) {
        // We're in the fiscal year that started this calendar year
        fiscalYearStart = currentYearFiscalStart;
        fiscalYearEnd = new Date(currentCalendarYear + 1, fiscalStartMonth, fiscalStartDay - 1);
      } else {
        // We're in the fiscal year that started last calendar year
        fiscalYearStart = new Date(currentCalendarYear - 1, fiscalStartMonth, fiscalStartDay);
        fiscalYearEnd = new Date(currentCalendarYear, fiscalStartMonth, fiscalStartDay - 1);
      }
      
      return { yearStart: fiscalYearStart, yearEnd: fiscalYearEnd };
    }
  };

  // Calculate preview pro-rata when end date changes
  useEffect(() => {
    if (!endDate || isOngoing) {
      setPreviewProrata(null);
      return;
    }

    // Get fiscal year boundaries based on job settings
    const { yearStart, yearEnd } = getFiscalYearBoundaries();
    
    // Determine effective start date for pro-rata calculation
    // Priority: continuous_from_date > employment_start_date
    // If continuous employment started BEFORE fiscal year, use fiscal year start (full year entitlement)
    const continuousFrom = job.continuous_from_date ? new Date(job.continuous_from_date) : null;
    const employmentStart = job.employment_start_date ? new Date(job.employment_start_date) : null;
    
    let effectiveStart = yearStart;
    let startedBeforeFiscalYear = false;
    
    // Check continuous_from_date first (highest priority)
    if (continuousFrom) {
      if (continuousFrom <= yearStart) {
        // Continuous employment started before fiscal year - full year entitlement for start
        effectiveStart = yearStart;
        startedBeforeFiscalYear = true;
      } else {
        // Started mid-year
        effectiveStart = continuousFrom;
      }
    } else if (employmentStart) {
      // Fallback to employment_start_date
      if (employmentStart <= yearStart) {
        effectiveStart = yearStart;
        startedBeforeFiscalYear = true;
      } else {
        effectiveStart = employmentStart;
      }
    } else {
      // No dates set, assume full year
      startedBeforeFiscalYear = true;
    }
    
    const empEnd = new Date(endDate);
    
    // Check if end date is within the fiscal year
    if (empEnd >= yearStart && empEnd <= yearEnd) {
      const totalDays = Math.round((yearEnd - yearStart) / (1000 * 60 * 60 * 24)) + 1;
      const workedDays = Math.round((empEnd - effectiveStart) / (1000 * 60 * 60 * 24)) + 1;
      const ratio = Math.round((workedDays / totalDays) * 100);
      const totalEntitlement = balance?.entitlement?.total || (job.base_days + job.carried_over + job.extra_days);
      const prorataEntitlement = Math.round(totalEntitlement * ratio / 100 * 10) / 10;
      
      setPreviewProrata({
        ratio,
        totalEntitlement,
        prorataEntitlement,
        workedDays: Math.round(workedDays),
        totalDays: Math.round(totalDays),
        fiscalYearStart: yearStart.toISOString().split('T')[0],
        fiscalYearEnd: yearEnd.toISOString().split('T')[0],
        effectiveStart: effectiveStart.toISOString().split('T')[0],
        startedBeforeFiscalYear
      });
    } else {
      setPreviewProrata(null);
    }
  }, [endDate, isOngoing, balance, job]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError(null);

    try {
      await api.put(`/employment/jobs/${job.id}`, {
        employment_end_date: isOngoing ? null : endDate || null,
        is_ongoing: isOngoing
      });
      onSuccess();
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to update employment');
    } finally {
      setSaving(false);
    }
  };

  const handleClearEndDate = () => {
    setEndDate('');
    setIsOngoing(true);
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content employment-change-modal" onClick={e => e.stopPropagation()}>
        <h2>📅 Plan Employment Change</h2>
        
        {error && <div className="error-message">{error}</div>}

        <div className="job-info-header">
          <span className="job-name">{job.job_name}</span>
          {job.company_name && <span className="company-name">at {job.company_name}</span>}
        </div>

        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label className="checkbox-label">
              <input
                type="checkbox"
                checked={isOngoing}
                onChange={(e) => {
                  setIsOngoing(e.target.checked);
                  if (e.target.checked) setEndDate('');
                }}
              />
              <span>Employment is ongoing (no end date)</span>
            </label>
          </div>

          {!isOngoing && (
            <div className="form-group">
              <label>Last Day of Employment</label>
              <input
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                min={job.employment_start_date || undefined}
                required={!isOngoing}
              />
              <p className="field-hint">
                This is your last working day. Leave will be calculated pro-rata.
              </p>
            </div>
          )}

          {/* Pro-rata preview */}
          {previewProrata && (
            <div className="prorata-preview">
              <h4>📊 Leave Impact Preview</h4>
              <div className="preview-content">
                <div className="preview-row fiscal-period">
                  <span className="preview-label">Fiscal year:</span>
                  <span className="preview-value">
                    {new Date(previewProrata.fiscalYearStart).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })} → {new Date(previewProrata.fiscalYearEnd).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}
                  </span>
                </div>
                {previewProrata.startedBeforeFiscalYear && (
                  <div className="preview-row continuous-note">
                    <span className="preview-label">✓ Employment started before fiscal year</span>
                  </div>
                )}
                <div className="preview-row">
                  <span className="preview-label">Working period:</span>
                  <span className="preview-value">
                    {new Date(previewProrata.effectiveStart).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })} → {new Date(endDate).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })}
                    {' '}({previewProrata.workedDays} of {previewProrata.totalDays} days, {previewProrata.ratio}%)
                  </span>
                </div>
                <div className="preview-row">
                  <span className="preview-label">Full year entitlement:</span>
                  <span className="preview-value original">{previewProrata.totalEntitlement} days</span>
                </div>
                <div className="preview-row highlight">
                  <span className="preview-label">Pro-rata entitlement:</span>
                  <span className="preview-value prorata">{previewProrata.prorataEntitlement} days</span>
                </div>
                {balance?.usage?.total_taken > previewProrata.prorataEntitlement && (
                  <div className="preview-warning">
                    ⚠️ You have already used {balance.usage.total_taken} days - more than your pro-rata entitlement!
                  </div>
                )}
              </div>
            </div>
          )}

          {job.employment_end_date && (
            <div className="current-end-date-info">
              <p>Current end date: <strong>{new Date(job.employment_end_date).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}</strong></p>
              <button type="button" className="btn-link" onClick={handleClearEndDate}>
                Remove end date
              </button>
            </div>
          )}

          <div className="modal-actions">
            <button type="button" className="btn-secondary" onClick={onClose}>
              Cancel
            </button>
            <button type="submit" className="btn-primary" disabled={saving}>
              {saving ? 'Saving...' : 'Save Changes'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default TimeOff;
